import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQhseDashboard } from './dashboard/hooks/useQhseDashboard'
import KpiCards from './dashboard/KpiCards'
import QuickActions, { actionRoutes } from './dashboard/QuickActions'
import DefectsOverview from './dashboard/DefectsOverview'
import NearMissSection from './dashboard/NearMissSection'
import AuditInspectionStatus from './dashboard/AuditInspectionStatus'
import CertificateWorkPermitPanel from './dashboard/CertificateWorkPermitPanel'
import {
  KpiDetailModal,
  DefectModal,
  NearMissModal,
  AuditModal,
  InspectionModal,
  WorkPermitModal,
} from './dashboard/DashboardModals'
import type { ModalType } from './dashboard/types'

import { KpiCardSkeleton, TableSkeleton } from '../../../shared/components/LoadingSkeletons'

/* ─── Timestamp ─── */
const nowStr = () => {
  const d = new Date()
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + ' UTC'
}

/* ─── Main Component ─── */
const QhseDashboard: FC = () => {
  const navigate = useNavigate()
  const { loading, data, kpis, refresh } = useQhseDashboard()

  const [modal, setModal] = useState<ModalType>(null)
  const [actionModal, setActionModal] = useState<string | null>(null)

  /* ─── Modal handlers ─── */
  const handleKpiClick = (kpi: string) => {
    setModal('kpiDetail')
  }

  const handleActionClick = (label: string) => {
    setActionModal(label)
  }

  if (loading) {
    return (
      <div className='flex flex-col flex-1 min-h-0'>
        <div className='flex flex-col flex-1 px-5 xl:px-10 pt-5 pb-8 gap-5 md:gap-6'>
          {/* Header Skeleton */}
          <div className='flex justify-between items-center'>
            <div>
              <div className='placeholder-wave'>
                <div className='placeholder rounded' style={{ width: 150, height: 22 }} />
              </div>
              <div className='placeholder-wave mt-1'>
                <div className='placeholder rounded' style={{ width: 180, height: 12 }} />
              </div>
            </div>
            <div className='placeholder-wave'>
              <div className='placeholder rounded' style={{ width: 60, height: 16 }} />
            </div>
          </div>

          {/* Quick Actions Skeleton */}
          <div>
            <div className='placeholder-wave mb-3'>
              <div className='placeholder rounded' style={{ width: 90, height: 12 }} />
            </div>
            <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3'>
              {Array.from({ length: 11 }).map((_, i) => (
                <div key={i} className='flex flex-col items-center gap-2 p-3 bg-white rounded-xl border border-slate-200 shadow-sm'>
                  <div className='placeholder-wave'>
                    <div className='placeholder rounded-lg' style={{ width: 32, height: 32 }} />
                  </div>
                  <div className='placeholder-wave w-full flex justify-center'>
                    <div className='placeholder rounded' style={{ width: '70%', height: 12 }} />
                  </div>
                  <div className='placeholder-wave w-full flex justify-center'>
                    <div className='placeholder rounded' style={{ width: '85%', height: 10 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* KPI Cards Skeleton */}
          <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 md:gap-6'>
            {Array.from({ length: 6 }).map((_, i) => (
              <KpiCardSkeleton key={i} />
            ))}
          </div>

          {/* Section 1: Defects + Near Misses Skeleton */}
          <div className='grid grid-cols-1 xl:grid-cols-12 gap-5 md:gap-6'>
            <div className='xl:col-span-7 flex flex-col'>
              <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
                <div className='flex items-center justify-between mb-3'>
                  <div className='placeholder-wave'>
                    <div className='placeholder rounded' style={{ width: 130, height: 16 }} />
                  </div>
                  <div className='placeholder-wave'>
                    <div className='placeholder rounded' style={{ width: 50, height: 14 }} />
                  </div>
                </div>
                <div className='overflow-x-auto'>
                  <table className='w-full text-[11px]'>
                    <thead>
                      <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
                        <th className='text-left font-semibold py-2 pr-2'>Vessel</th>
                        <th className='text-left font-semibold py-2 px-2'>Description</th>
                        <th className='text-left font-semibold py-2 px-2'>Category</th>
                        <th className='text-left font-semibold py-2 px-2'>Date</th>
                        <th className='text-right font-semibold py-2 pl-2'>Status</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-slate-100'>
                      <TableSkeleton rows={5} cols={5} />
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className='xl:col-span-5 flex flex-col'>
              <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
                <div className='flex items-center justify-between mb-3'>
                  <div className='placeholder-wave'>
                    <div className='placeholder rounded' style={{ width: 130, height: 16 }} />
                  </div>
                  <div className='placeholder-wave'>
                    <div className='placeholder rounded' style={{ width: 50, height: 14 }} />
                  </div>
                </div>
                <div className='overflow-x-auto'>
                  <table className='w-full text-[11px]'>
                    <thead>
                      <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
                        <th className='text-left font-semibold py-2 pr-2'>Date</th>
                        <th className='text-left font-semibold py-2 px-2'>Vessel</th>
                        <th className='text-left font-semibold py-2 px-2'>Description</th>
                        <th className='text-right font-semibold py-2 pl-2'>Status</th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-slate-100'>
                      <TableSkeleton rows={5} cols={4} />
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Audits + Inspections Skeleton */}
          <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
            <div className='flex items-center justify-between mb-3'>
              <div className='placeholder-wave'>
                <div className='placeholder rounded' style={{ width: 120, height: 16 }} />
              </div>
              <div className='placeholder-wave'>
                <div className='placeholder rounded' style={{ width: 50, height: 14 }} />
              </div>
            </div>
            <div className='overflow-x-auto'>
              <table className='w-full text-[11px]'>
                <thead>
                  <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
                    <th className='text-left font-semibold py-2 pr-2'>Vessel</th>
                    <th className='text-left font-semibold py-2 px-2'>Type</th>
                    <th className='text-left font-semibold py-2 px-2'>Date</th>
                    <th className='text-right font-semibold py-2 pl-2'>Status</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  <TableSkeleton rows={4} cols={4} />
                </tbody>
              </table>
            </div>
          </div>

          <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
            <div className='flex items-center justify-between mb-3'>
              <div className='placeholder-wave'>
                <div className='placeholder rounded' style={{ width: 130, height: 16 }} />
              </div>
              <div className='placeholder-wave'>
                <div className='placeholder rounded' style={{ width: 50, height: 14 }} />
              </div>
            </div>
            <div className='overflow-x-auto'>
              <table className='w-full text-[11px]'>
                <thead>
                  <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
                    <th className='text-left font-semibold py-2 pr-2'>Vessel</th>
                    <th className='text-left font-semibold py-2 px-2'>Type</th>
                    <th className='text-left font-semibold py-2 px-2'>Date</th>
                    <th className='text-right font-semibold py-2 pl-2'>Status</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  <TableSkeleton rows={4} cols={4} />
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 3: Certificates + Work Permits + Compliance Skeleton */}
          <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
            <div className='flex items-center justify-between mb-3'>
              <div className='placeholder-wave'>
                <div className='placeholder rounded' style={{ width: 140, height: 16 }} />
              </div>
            </div>
            <div className='py-6 flex flex-col items-center gap-2'>
              <div className='placeholder-wave w-full max-w-sm'>
                <div className='placeholder rounded w-full' style={{ height: 14 }} />
              </div>
              <div className='placeholder-wave w-full max-w-xs'>
                <div className='placeholder rounded w-full' style={{ height: 12 }} />
              </div>
            </div>
          </div>

          <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
            <div className='flex items-center justify-between mb-3'>
              <div className='placeholder-wave'>
                <div className='placeholder rounded' style={{ width: 130, height: 16 }} />
              </div>
              <div className='placeholder-wave'>
                <div className='placeholder rounded' style={{ width: 50, height: 14 }} />
              </div>
            </div>
            <div className='overflow-x-auto'>
              <table className='w-full text-[11px]'>
                <thead>
                  <tr className='text-slate-400 text-[10px] uppercase tracking-wider'>
                    <th className='text-left font-semibold py-2 pr-2'>Permit</th>
                    <th className='text-left font-semibold py-2 px-2'>Vessel</th>
                    <th className='text-left font-semibold py-2 px-2'>Start</th>
                    <th className='text-right font-semibold py-2 pl-2'>Status</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-slate-100'>
                  <TableSkeleton rows={4} cols={4} />
                </tbody>
              </table>
            </div>
          </div>

          <div className='flex flex-col flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 md:p-5'>
            <div className='flex items-center justify-between mb-3'>
              <div className='placeholder-wave'>
                <div className='placeholder rounded' style={{ width: 130, height: 16 }} />
              </div>
            </div>
            <div className='py-6 flex flex-col items-center gap-2'>
              <div className='placeholder-wave w-full max-w-sm'>
                <div className='placeholder rounded w-full' style={{ height: 14 }} />
              </div>
              <div className='placeholder-wave w-full max-w-xs'>
                <div className='placeholder rounded w-full' style={{ height: 12 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col flex-1 min-h-0'>
      <div className='flex flex-col flex-1 px-5 xl:px-10 pt-5 pb-8 gap-5 md:gap-6'>

        {/* ── Page Header ── */}
        <div className='flex justify-between items-center'>
          <div>
            <h3 className='text-lg font-bold text-slate-900'>QHSE Dashboard</h3>
            <div className='text-[11px] text-slate-400'>{nowStr()}</div>
          </div>
          <button onClick={refresh} className='text-[11px] text-blue-600 hover:text-blue-800 font-semibold transition-colors'>
            ↻ Refresh
          </button>
        </div>

        {/* ── Quick Actions ── */}
        <QuickActions onAction={handleActionClick} />

        {/* ── KPI Cards ── */}
        <KpiCards loading={loading} kpis={kpis} onKpiClick={handleKpiClick} />

        {/* ── Section 1: Defects + Near Misses ── */}
        <div className='grid grid-cols-1 xl:grid-cols-12 gap-5 md:gap-6'>
          <div className='xl:col-span-7 flex flex-col'>
            <DefectsOverview
              defects={data.defects}
              onViewAll={() => setModal('defects')}
            />
          </div>
          <div className='xl:col-span-5 flex flex-col'>
            <NearMissSection
              nearMisses={data.nearMisses}
              onViewAll={() => setModal('nearMisses')}
            />
          </div>
        </div>

        {/* ── Section 2: Audits + Inspections ── */}
        <AuditInspectionStatus
          audits={data.audits}
          inspections={data.inspections}
          onViewAudits={() => setModal('audits')}
          onViewInspections={() => setModal('inspections')}
        />

        {/* ── Section 3: Certificates + Work Permits + Compliance ── */}
        <CertificateWorkPermitPanel
          workPermits={data.workPermits}
          onViewWorkPermits={() => setModal('workPermits')}
        />
      </div>

      {/* ── Action Confirmation Modal ── */}
      {actionModal && (
        <div className='fixed inset-0 z-[1050] flex items-center justify-center p-4'>
          <div className='fixed inset-0 bg-black/40' onClick={() => setActionModal(null)} />
          <div className='relative bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-sm p-6'>
            <h3 className='text-sm font-bold text-slate-900 mb-2'>Navigate to {actionModal}?</h3>
            <p className='text-[11px] text-slate-500 mb-4'>This action will redirect you to the {actionModal} page.</p>
            <div className='flex justify-end gap-2'>
              <button onClick={() => setActionModal(null)} className='px-4 py-2 text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors'>
                Cancel
              </button>
              <button onClick={() => { setActionModal(null); navigate(actionRoutes[actionModal] || '/qhse') }}
                className='px-4 py-2 text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors'>
                Go to page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      <KpiDetailModal open={modal === 'kpiDetail'} onClose={() => setModal(null)} kpis={kpis} />
      <DefectModal open={modal === 'defects'} onClose={() => setModal(null)} defects={data.defects} />
      <NearMissModal open={modal === 'nearMisses'} onClose={() => setModal(null)} nearMisses={data.nearMisses} />
      <AuditModal open={modal === 'audits'} onClose={() => setModal(null)} audits={data.audits} />
      <InspectionModal open={modal === 'inspections'} onClose={() => setModal(null)} inspections={data.inspections} />
      <WorkPermitModal open={modal === 'workPermits'} onClose={() => setModal(null)} workPermits={data.workPermits} />
    </div>
  )
}

export default QhseDashboard
